import { useState } from 'react';
import { Box, alpha, TextField, Typography } from '@material-ui/core';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { plural, t, Trans } from '@lingui/macro';
import Button from '@material-ui/core/Button';
import { i18n } from '@lingui/core';
import { RowFlexLayout } from '../../layout';
import { splitEmailString } from '../../../utilities/StringUtilities';
import { useDataService } from '../../../services/ApiDataService';
import { isEmailValid } from '../../../utilities/FormUtilities';
import { usePortalLocation, usePrimaryXMSLocation } from '../../../stores/EnvironmentStore';

export const useStyles = makeStyles((theme) => ({
    emailSuccess: {
        fontWeight: 500,
        backgroundColor: alpha(theme.palette.status.green, 0.1),
        '& svg': {
            marginRight: theme.spacing(2),
            color: theme.palette.status.green,
        },
    },
    emailFailure: {
        fontWeight: 500,
        backgroundColor: alpha(theme.palette.status.red, 0.1),
        '& svg': {
            marginRight: theme.spacing(2),
            color: theme.palette.status.red,
        },
    },
}));

const fieldName = 'instructionAddresses';
const sendButtonText = i18n._(t`Send`);

function getInvalidEmails(emailArray) {
    if (!emailArray) {
        return false;
    }

    return _.filter(emailArray, (email) => !isEmailValid(email));
}

/**
 * @param {any} dataService
 * @param {string} primaryXmsLocation
 * @param {string} portalLocation
 * @param {import('./AgentDownloadDialog').WizardSelectionsRef} wizardSelections
 * @param {string[]} emailArray
 * @returns {Promise<void>}
 */
function sendEmails(dataService, primaryXmsLocation, portalLocation, wizardSelections, emailArray) {
    const emailApiUrl = '/ipm/v1/email/virtualpulse';
    const requestBody = {
        origin: window.location.origin,
        xmshost: primaryXmsLocation,
        uihost: portalLocation,
        toEmail: emailArray,
        sendType: wizardSelections.current.os,
        sendModel: wizardSelections.current.model,
        linuxGetter: wizardSelections.current.httpClient,
    };

    return dataService.post(emailApiUrl, requestBody);
}

/** @param {import('./AgentDownloadDialog').WizardProps} props */
export default function AgentDownloadWizardEmail({ wizardSelections }) {
    const classes = useStyles();
    const dataService = useDataService();
    const [addresses, setAddresses] = useState('');
    const [addressError, setAddressError] = useState(false);
    const [addressErrorText, setAddressErrorText] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [primaryXmsLocation] = usePrimaryXMSLocation();
    const [portalLocation] = usePortalLocation();

    const handleChange = (e) => {
        const emails = e.target.value;
        setAddresses(emails);

        if (!_.isEmpty(emails)) {
            setShowError(false);
            setShowSuccess(false);
        }
    };

    const handleSendClick = () => {
        const emailsAsArray = splitEmailString(addresses);
        const invalidEmails = getInvalidEmails(emailsAsArray);
        if (invalidEmails.length > 0) {
            setAddressError(true);
            setAddressErrorText(
                i18n._(
                    plural(invalidEmails.length, {
                        one: `${_.first(invalidEmails)} is not a valid email address`,
                        other: `${invalidEmails.join(', ')} are not valid email addresses`,
                    })
                )
            );

            return;
        }

        return sendEmails(
            dataService,
            primaryXmsLocation,
            portalLocation,
            wizardSelections,
            splitEmailString(addresses)
        )
            .then(() => {
                setShowSuccess(true);
                setAddressError(false);
                setAddressErrorText(null);
                setAddresses('');
            })
            .catch(() => {
                setShowSuccess(false);
                setShowError(true);
            });
    };

    return (
        <>
            <Typography variant="body2" gutterBottom>
                <Trans>
                    These instructions can be sent via email by entering the email addresses below and clicking Send
                </Trans>
            </Typography>
            <Box mb={2}>
                <RowFlexLayout vCenter>
                    <Box>
                        <TextField
                            name={fieldName}
                            label={<Trans>Email Address(es) separated by a semicolon, comma, or space</Trans>}
                            onChange={handleChange}
                            value={addresses}
                            required
                            inputProps={{ size: 85 }}
                            error={addressError}
                            helperText={addressErrorText}
                        />
                    </Box>
                    <Box ml={2}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            disabled={!addresses}
                            onClick={handleSendClick}
                        >
                            {sendButtonText}
                        </Button>
                    </Box>
                </RowFlexLayout>

                {showSuccess && (
                    <Box className={classes.emailSuccess} p={1} mt={2}>
                        <RowFlexLayout vCenter>
                            <CheckCircleOutlineIcon />
                            <Box>
                                <Trans>Success!</Trans>
                            </Box>
                        </RowFlexLayout>
                    </Box>
                )}

                {showError && (
                    <Box className={classes.emailFailure} p={1} mt={2}>
                        <RowFlexLayout vCenter>
                            <ErrorOutlineIcon />
                            <Box>
                                <Trans>An error occurred sending emails. Please contact support.</Trans>
                            </Box>
                        </RowFlexLayout>
                    </Box>
                )}
            </Box>
        </>
    );
}
